import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';

const PipelineRunsPanel = ({ onLoadRun }) => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRun, setSelectedRun] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchRuns();
  }, []);

  const fetchRuns = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/admin/pipeline/runs/list/');
      if (response.data.success) {
        setRuns(response.data.runs);
      } else {
        setError('Failed to load pipeline runs');
      }
    } catch (err) {
      setError(err.message || 'Failed to load pipeline runs');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadRun = async (runId) => {
    try {
      setLoading(true);
      const response = await adminApi.get(`/admin/pipeline/runs/${runId}/`);
      if (response.data.success) {
        const run = response.data.run;
        if (onLoadRun) {
          onLoadRun(run);
        }
        setShowDetails(false);
      }
    } catch (err) {
      setError('Failed to load run details');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRun = async (runId) => {
    if (!window.confirm('Are you sure you want to delete this run?')) return;

    try {
      await adminApi.delete(`/admin/pipeline/runs/${runId}/`);
      fetchRuns();
    } catch (err) {
      setError('Failed to delete run');
    }
  };

  const handleViewDetails = async (runId) => {
    try {
      setLoading(true);
      const response = await adminApi.get(`/admin/pipeline/runs/${runId}/`);
      if (response.data.success) {
        setSelectedRun(response.data.run);
        setShowDetails(true);
      }
    } catch (err) {
      setError('Failed to load run details');
    } finally {
      setLoading(false);
    }
  };

  const filteredRuns = runs.filter(run =>
    run.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    run.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    run.case_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    run.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    run.provider_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const truncateText = (text, maxLength = 300) => {
    if (!text) return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (showDetails && selectedRun) {
    return (
      <div className="pipeline-runs-details">
        <button className="btn btn-link mb-3 p-1" onClick={() => setShowDetails(false)}>
          <i className="fas fa-arrow-left me-1"></i> Back to list
        </button>

        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h5 className="p-2">{selectedRun.name}</h5>
            {selectedRun.description && (
              <p className="text-muted small mb-0">{selectedRun.description}</p>
            )}
          </div>
          <span className={`badge ${selectedRun.execution_metadata?.success ? 'bg-success' : 'bg-danger'}`}>
            {selectedRun.execution_metadata?.success ? 'Success' : 'Failed'}
          </span>
        </div>

        <div className="card mb-3">
          <div className="card-body p-3">
            <div className="row g-2 small">
              <div className="col-6">
                <strong>Created:</strong> {formatDate(selectedRun.created_at)}
              </div>
              {selectedRun.patient_name && (
                <div className="col-6">
                  <strong>Patient:</strong> {selectedRun.patient_name}
                </div>
              )}
              {selectedRun.case_id && (
                <div className="col-6">
                  <strong>Case ID:</strong> {selectedRun.case_id}
                </div>
              )}
              {selectedRun.provider_name && (
                <div className="col-6">
                  <strong>Provider:</strong> {selectedRun.provider_name}
                </div>
              )}
              {selectedRun.template_name && (
                <div className="col-6">
                  <strong>Template:</strong> {selectedRun.template_name}
                </div>
              )}
              {selectedRun.llm_prompt_template_name && (
                <div className="col-6">
                  <strong>LLM Prompt Template:</strong> {selectedRun.llm_prompt_template_name}
                </div>
              )}
              {selectedRun.execution_metadata?.model_used && (
                <div className="col-6">
                  <strong>Model:</strong> {selectedRun.execution_metadata.model_used}
                </div>
              )}
              {selectedRun.execution_metadata?.duration_ms && (
                <div className="col-6">
                  <strong>Duration:</strong> {selectedRun.execution_metadata.duration_ms}ms
                </div>
              )}
              {selectedRun.tags && selectedRun.tags.length > 0 && (
                <div className="col-12">
                  <strong>Tags:</strong>{' '}
                  {selectedRun.tags.map((tag, i) => (
                    <span key={i} className="badge bg-secondary me-1">{tag}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {selectedRun.note_preview && (
          <div className="card mb-3">
            <div className="card-header py-2">
              <strong className="small">Note Preview</strong>
            </div>
            <div className="card-body p-3">
              <pre className="mb-0 small text-wrap" style={{ maxHeight: '200px', overflow: 'auto', fontSize: '0.8rem' }}>
                {selectedRun.note_preview}
              </pre>
            </div>
          </div>
        )}

        {selectedRun.system_message && (
          <div className="card mb-3">
            <div className="card-header py-2">
              <strong className="small">System Message</strong>
            </div>
            <div className="card-body p-3">
              <pre className="mb-0 small text-wrap" style={{ maxHeight: '200px', overflow: 'auto', fontSize: '0.8rem' }}>
                {truncateText(selectedRun.system_message, 500)}
              </pre>
            </div>
          </div>
        )}

        {selectedRun.user_message && (
          <div className="card mb-3">
            <div className="card-header py-2">
              <strong className="small">User Message</strong>
            </div>
            <div className="card-body p-3">
              <pre className="mb-0 small text-wrap" style={{ maxHeight: '200px', overflow: 'auto', fontSize: '0.8rem' }}>
                {truncateText(selectedRun.user_message, 500)}
              </pre>
            </div>
          </div>
        )}

        <div className="d-flex gap-2">
          <button
            className="btn btn-primary btn-sm"
            onClick={() => handleLoadRun(selectedRun.id)}
          >
            <i className="fas fa-play me-1"></i> Load into Sandbox
          </button>
          <button
            className="btn btn-outline-danger btn-sm"
            onClick={() => {
              handleDeleteRun(selectedRun.id);
              setShowDetails(false);
            }}
          >
            <i className="fas fa-trash me-1"></i> Delete
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pipeline-runs-panel">
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      
      <div className="mb-3 flex justify-content-between gap-2">
        <input
          type="text"
          className="form-control"
          placeholder="Search runs with case ID, provider name, or patient name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button className="btn btn-sm btn-secondary" onClick={fetchRuns} disabled={loading}>
          <i className="fas fa-sync-alt me-1"></i>
        </button>
      </div>

      {loading ? (
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <>
        <div className="list-group">
          {filteredRuns.length === 0 ? (
            <div className="text-center text-muted py-4">
              No pipeline runs found. FYI - Runs older than 30 days are automatically deleted at midnight UTC.
            </div>
          ) : (
            filteredRuns.map((run) => (
              <div key={run.id} className="list-group-item list-group-item-action">
                <div className="d-flex w-100 justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <h6 className="mb-1">{run.name}</h6>
                    {run.description && (
                      <p className="mb-1 small text-muted">{run.description}</p>
                    )}
                    <div className="small">
                      {run.patient_name && (
                        <span className="text-muted">Patient: {run.patient_name}</span>
                      )}
                      {run.case_id && (
                        <span className="text-muted ms-2">• Case: {run.case_id.substring(0, 8)}</span>
                      )}
                      {run.provider_name && (
                        <span className="text-muted ms-2">• Provider: {run.provider_name}</span>
                      )}
                      {run.execution_metadata?.model_used && (
                        <span className="text-muted ms-2">• {run.execution_metadata.model_used}</span>
                      )}
                    </div>
                    {run.tags && run.tags.length > 0 && (
                      <div className="mt-1">
                        {run.tags.map((tag, i) => (
                          <span key={i} className="badge bg-secondary me-1" style={{ fontSize: '0.7rem' }}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="ms-2">
                    <span className={`badge ${run.execution_metadata?.success ? 'bg-success' : 'bg-danger'}`}>
                      {run.execution_metadata?.success ? '✓' : '✗'}
                    </span>
                  </div>
                </div>
                <div className="mt-2 d-flex gap-2">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => handleViewDetails(run.id)}
                  >
                    <i className="fas fa-eye me-1"></i> View
                  </button>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => handleLoadRun(run.id)}
                  >
                    <i className="fas fa-play me-1"></i> Load
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDeleteRun(run.id)}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
        <p className='small p-2'>Runs older than 30 days are automatically deleted at midnight UTC.</p>
        </>
      )}
    </div>
  );
};

export default PipelineRunsPanel;
